import React, { useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  accessRightsShape,
  ActionBar,
  ActionBarButtons,
  apiShape,
  browserShape,
  Column,
  Container,
  DeleteActionButton,
  Details,
  DetailsHeaderItem,
  DetailsItem,
  EditActionButton,
  FormattedRelativeTime,
  Heading,
  intlShape,
  Loader,
  More,
  notifierShape,
  parseISO8601Date,
  PictureGallery,
  pullBooleanFromSpec,
  pullValueFromSpec,
  Row,
  userShape,
  withAccessRights,
  withApi,
  withBrowser,
  withFlipper,
  withIntl,
  withNotifier,
  withSession,
  withUser,
} from "lcm-iot-commons";

import {
  edgeDeviceIconClassName,
  edgeDeviceStatus,
  edgeDeviceStatusIconClassName,
  fieldGatewayEnableStatusIconClassName,
  lastSeenStatus,
  lcmIotIconCirculeRedGreenIcon,
} from "../../utils/statusUtils";

import FieldGateways from "./FieldGateways";
import NetworkInterfaces from "./NetworkInterfaces";
import EdgeDeviceApplications from "./EdgeDeviceApplications";
import UpdateSchedules from "./UpdateSchedules";
import { getVersionsWithCertainStatus } from "../../utils/edgeDeviceUpdateSchedulesUtils";
import { getEdgeDeviceImageUrl } from "./PicturePoolUrl";

import { EdgeDeviceTypes } from "./EdgeDeviceConstants";
import { EdgeDeviceDetailsTechnicalUser } from "./EdgeDeviceDetailsTechnicalUser";

export const convertSecondsToMinutes = (sec: number) =>
  sec ? Math.round(sec / 60) : null;

export const convertMillisecoundsToMinutes = (ms: number) =>
  ms ? Math.round(ms / 1000 / 60) : null;

export const unlinkEdgeDevice = async ({
  api,
  notifier,
  intl,
  browser,
  edgeDeviceId,
}: {
  api: apiShape;
  notifier: notifierShape;
  intl: intlShape;
  browser: browserShape;
  edgeDeviceId?: string;
}): Promise<void> => {
  if (!edgeDeviceId) {
    return;
  }

  try {
    await api.patch(`/edm/edge_devices/${edgeDeviceId}/unlink`);
    notifier.showSuccess(
      intl.formatMessage({ id: "edge_device.delete.successful" }),
    );
    browser.navigateTo("/edge_devices");
  } catch (error) {
    notifier.showError(api.translateError(error));
  }
};

const extractEthernetipSupported = (response: $TSFixMe) =>
  response.software_version?.specifications
    ? pullBooleanFromSpec(response.software_version.specifications, [
        "ethernetip_supported",
      ])
    : false;

const extractProfinetSupported = (response: $TSFixMe) =>
  response.software_version?.specifications
    ? pullBooleanFromSpec(response.software_version.specifications, [
        "profinet_supported",
      ])
    : false;

const isSGC200Device = (edgeDevice: $TSFixMe): boolean =>
  !!edgeDevice && edgeDevice.type?.code === EdgeDeviceTypes.SGC200;

const isSGC500Device = (edgeDevice: $TSFixMe): boolean =>
  !!edgeDevice &&
  (edgeDevice.type?.code === EdgeDeviceTypes.SGC500_AA ||
    edgeDevice.type?.code === EdgeDeviceTypes.SGC500_AB ||
    edgeDevice.type?.code === EdgeDeviceTypes.SGC500_AB_24 ||
    edgeDevice.type?.code === EdgeDeviceTypes.SGC500_AC ||
    edgeDevice.type?.code === EdgeDeviceTypes.SGC500_SW);

const isLogLevelEnabled = (edgeDevice: $TSFixMe): boolean =>
  !isSGC200Device(edgeDevice);

const areFieldGatewaysEnabled = (edgeDevice: $TSFixMe): boolean =>
  !!edgeDevice && edgeDevice.type?.code !== EdgeDeviceTypes.SGC200;

const areNetworkInterfacesEnabled = (edgeDevice: $TSFixMe): boolean =>
  !!edgeDevice &&
  edgeDevice.type?.code !== EdgeDeviceTypes.SGC200 &&
  edgeDevice.type?.code !== EdgeDeviceTypes.SGC500_SW &&
  edgeDevice.networkInterfaces.length > 0;

const isEdgeDeviceNotificationsOfflineStatusActive = (
  statusOfflineValue: $TSFixMe,
  userId: number,
) => statusOfflineValue.split(";").includes(userId.toString());

const transformBasicData = (edgeJson: $TSFixMe) => ({
  last_seen_at: edgeJson.last_seen_at
    ? parseISO8601Date(edgeJson.last_seen_at)
    : null,

  apply_timestamp: edgeJson.apply_timestamp
    ? parseISO8601Date(edgeJson.apply_timestamp)
    : null,

  ethernetip_supported: extractEthernetipSupported(edgeJson),
  ethernet_ip_active: pullBooleanFromSpec(edgeJson.specifications, [
    "eh.user_config.ethernet_ip_active",
  ]),

  profinet_supported: extractProfinetSupported(edgeJson),
  profinet_active: pullBooleanFromSpec(edgeJson.specifications, [
    "eh.user_config.profinet_active",
  ]),

  netilion_health_active:
    pullBooleanFromSpec(edgeJson.specifications, [
      "eh.user_config.feature.netilion_health_active",
    ]) ?? true,
  netilion_value_active:
    pullBooleanFromSpec(edgeJson.specifications, [
      "eh.user_config.feature.netilion_value_active",
    ]) ?? true,
});

const transformIntervalsSGC200 = (specifications: $TSFixMe) => ({
  data_collection_interval: convertSecondsToMinutes(
    pullValueFromSpec(specifications, [
      "eh.user_config.data_collection_interval",
    ]),
  ),
  data_send_interval: convertSecondsToMinutes(
    pullValueFromSpec(specifications, ["eh.user_config.data_send_interval"]),
  ),
});

const transformIntervalsSGC500 = (specifications: $TSFixMe) => ({
  health_data_collection_interval: convertMillisecoundsToMinutes(
    pullValueFromSpec(specifications, [
      "eh.user_config.settings.health.dvmonitor_interval",
    ]),
  ),
  measurement_value_data_collection_interval: convertMillisecoundsToMinutes(
    pullValueFromSpec(specifications, [
      "eh.user_config.settings.valueservice.dvmonitor_interval",
    ]),
  ),
});

const showLabelMinutes = (intl: $TSFixMe, value: number) => {
  const labelId = value > 1 ? "label.minutes" : "label.minute";
  return `${value} ${intl.formatMessage({ id: labelId })}`;
};

const showLabelActiveInactive = (state: boolean) =>
  state ? "label.active" : "label.inactive";

const showLabelMinuteSGC200 = (intl: $TSFixMe, value: number) =>
  value ? showLabelMinutes(intl, value) : "-";

const showLabelMinuteSGC500 = (intl: $TSFixMe, value: number) =>
  value
    ? showLabelMinutes(intl, value)
    : `< 1 ${intl.formatMessage({ id: "label.minute" })}`;

const transformRelatedData = (
  edgeJson: $TSFixMe,
  fieldGateways: $TSFixMe,
  networkInterfaces: $TSFixMe,
  edgeDeviceApplications: $TSFixMe,
  updateSchedules: $TSFixMe,
) => ({
  client_application: edgeJson.api_subscription?.client_application,
  tenant: edgeJson.tenant?.name,
  usergroup: edgeJson.usergroup?.name,
  fieldGateways: fieldGateways?.field_gateways,
  networkInterfaces: networkInterfaces?.network_interfaces,
  edgeDeviceApplications: edgeDeviceApplications?.edge_device_applications,

  updateSchedules: getVersionsWithCertainStatus(
    updateSchedules?.update_schedules,
  ),
});

const transformEdgeDeviceData = (
  edgeJson: $TSFixMe,
  fieldGateways: $TSFixMe,
  networkInterfaces: $TSFixMe,
  edgeDeviceApplications: $TSFixMe,
  updateSchedules: $TSFixMe,
  user: $TSFixMe,
) => {
  const data = {
    ...edgeJson,
    ...transformBasicData(edgeJson),
    ...transformIntervalsSGC200(edgeJson.specifications),
    ...transformIntervalsSGC500(edgeJson.specifications),
    ...transformRelatedData(
      edgeJson,
      fieldGateways,
      networkInterfaces,
      edgeDeviceApplications,
      updateSchedules,
    ),
    edgeDeviceImageUrl: getEdgeDeviceImageUrl(edgeJson.type.code, 500),
    isThirdPartyType:
      edgeJson?.type && edgeJson.type.code === "third_party_edge_device",
    node: edgeJson?.default_node,
  };

  const statusOfflineValue = pullValueFromSpec(edgeJson.specifications, [
    "eh.user_config.user_notifications.status_offline",
  ]);

  data.isEdgeDeviceNotificationsOfflineStatusActive =
    statusOfflineValue &&
    user?.id &&
    isEdgeDeviceNotificationsOfflineStatusActive(statusOfflineValue, user.id);

  return data;
};

const getIssueLabel = (edgeDevice: $TSFixMe) =>
  edgeDevice.isEdgeDeviceNotificationsOfflineStatusActive
    ? "label.active"
    : "label.inactive";

const renderEdgeDevicePicture = (edgeDevice: $TSFixMe) =>
  edgeDevice.edgeDeviceImageUrl ? (
    // @ts-expect-error TS(2322) FIXME: Type '{ additionalPictureUrls: any[]; }' is not as... Remove this comment to see the full error message
    <PictureGallery additionalPictureUrls={[edgeDevice.edgeDeviceImageUrl]} />
  ) : (
    // @ts-expect-error TS(2322) FIXME: Type '{ placeholder: string; id: string; }' is not... Remove this comment to see the full error message
    <PictureGallery placeholder={edgeDeviceIconClassName()} id="placeholder" />
  );

const renderEdgeDeviceStatus = (edgeDevice: $TSFixMe) =>
  !edgeDevice?.isThirdPartyType && (
    <DetailsItem
      id="edge-device-status"
      icon={edgeDeviceStatusIconClassName(
        edgeDevice?.status?.code,
        edgeDevice?.last_seen_at,
      )}
      translationKey="label.status"
      value={edgeDevice?.status?.name}
    />
  );

const renderEdgeDeviceNotifications = (edgeDevice: $TSFixMe, intl: $TSFixMe) =>
  !edgeDevice?.isThirdPartyType && (
    <DetailsItem
      id="edge-device-notifications"
      icon={lcmIotIconCirculeRedGreenIcon(
        edgeDevice?.isEdgeDeviceNotificationsOfflineStatusActive,
      )}
      translationKey="label.notifications"
      contextHelperTitle={intl.formatMessage({
        id: "edge_device.user_notification.offline.hint_heading",
      })}
      contextHelperChildren={
        intl.formatMessage({
          id: "edge_device.user_notification.offline.hint_text",
        }) +
        intl.formatMessage({
          id: "edge_device.user_notification.offline.hint_text2",
        })
      }
    >
      <FormattedMessage id={getIssueLabel(edgeDevice)} />
    </DetailsItem>
  );

const renderEdgeDeviceLoglevel = (edgeDevice: $TSFixMe, intl: $TSFixMe) =>
  !edgeDevice?.isThirdPartyType &&
  isLogLevelEnabled(edgeDevice) && (
    <DetailsItem
      id="edge-device-log-level"
      translationKey="label.log_level"
      value={
        edgeDevice.log_level
          ? intl.formatMessage({
              id: `enum.edge_device_log_level.${edgeDevice.log_level.toLowerCase()}`,
            })
          : null
      }
    />
  );

const getLastSeenTextColor = (edgeDeviceData: $TSFixMe) =>
  edgeDeviceStatus(edgeDeviceData.status.code, edgeDeviceData.last_seen_at) ===
  "critical"
    ? "lastseen-offline"
    : "";

const renderEdgeDeviceLastSeenAt = (edgeDevice: $TSFixMe) =>
  !edgeDevice?.isThirdPartyType && (
    <DetailsItem
      id="edge-device-last-seen"
      translationKey="label.last_seen"
      icon={lastSeenStatus(edgeDevice?.last_seen_at)}
    >
      {edgeDevice?.last_seen_at ? (
        <span className={getLastSeenTextColor(edgeDevice)}>
          <FormattedRelativeTime value={new Date(edgeDevice.last_seen_at)} />
        </span>
      ) : /* istanbul ignore next */ null}
    </DetailsItem>
  );

const renderEdgeDeviceTenant = (edgeDevice: $TSFixMe) =>
  !edgeDevice?.isThirdPartyType && (
    <DetailsItem
      id="edge-device-tenant"
      translationKey="label.tenant"
      value={edgeDevice?.tenant}
    />
  );

const renderEdgeDeviceNodes = (edgeDevice: $TSFixMe) =>
  edgeDevice?.isThirdPartyType && (
    <DetailsItem
      id="edge-device-node"
      translationKey="label.nodes"
      value={edgeDevice.node?.name}
    />
  );

const renderEdgeDeviceLastApplied = (edgeDevice: $TSFixMe) =>
  !edgeDevice?.isThirdPartyType && (
    <DetailsItem
      id="edge-device-last-applied"
      translationKey="label.last_applied"
    >
      {edgeDevice.apply_timestamp ? (
        <FormattedRelativeTime value={new Date(edgeDevice.apply_timestamp)} />
      ) : /* istanbul ignore next */ null}
    </DetailsItem>
  );

const renderEdgeDeviceSoftwareVersion = (edgeDevice: $TSFixMe) =>
  !edgeDevice?.isThirdPartyType && (
    <DetailsItem
      id="edge-device-software-version"
      translationKey="label.software_version"
      value={edgeDevice.software_version?.version_name}
    />
  );

const renderEipActivationStatus = (edgeDevice: $TSFixMe) =>
  edgeDevice?.ethernetip_supported && (
    <DetailsItem
      id="eip-activation-status"
      translationKey="label.eip.activation_status"
      icon={fieldGatewayEnableStatusIconClassName(
        edgeDevice.ethernet_ip_active,
      )}
    >
      <FormattedMessage
        id={showLabelActiveInactive(edgeDevice.ethernet_ip_active)}
      />
    </DetailsItem>
  );

const renderProfinetActivationStatus = (edgeDevice: $TSFixMe) =>
  edgeDevice?.profinet_supported && (
    <DetailsItem
      id="profinet-activation-status"
      translationKey="label.profinet.activation_status"
      icon={fieldGatewayEnableStatusIconClassName(edgeDevice?.profinet_active)}
    >
      <FormattedMessage
        id={showLabelActiveInactive(edgeDevice?.profinet_active)}
      />
    </DetailsItem>
  );

const renderFieldGateways = (edgeDevice: $TSFixMe) =>
  !edgeDevice?.isThirdPartyType &&
  areFieldGatewaysEnabled(edgeDevice) && (
    <FieldGateways
      fieldGateways={edgeDevice?.fieldGateways}
      edgeDevice={edgeDevice}
    />
  );

type EdgeDeviceDetailsProps = {
  intl: intlShape;
  accessRights: accessRightsShape;
  user: userShape;
  match: {
    params?: {
      id?: string;
      edgeDeviceId?: string;
    };
  };
  api: apiShape;
  notifier: notifierShape;
  browser: browserShape;
  flipper: {
    edgeDeviceDataCollectionInterval?: boolean;
    edgeDeviceActiveFeatures?: boolean;
    edgeDeviceLateBinding?: boolean;
  };
};

export function EdgeDeviceDetails({
  intl,
  accessRights,
  match,
  api,
  notifier,
  browser,
  flipper,
  user,
}: EdgeDeviceDetailsProps) {
  const [edgeDevice, setEdgeDevice] = useState<$TSFixMe | null>(null);
  const edgeDeviceId = match.params?.id;

  const loadEdgeDevice = async () => {
    if (!edgeDeviceId) {
      return;
    }

    try {
      const [
        edgeJson,
        fieldGateways,
        networkInterfaces,
        edgeDeviceApplications,
        updateSchedules,
      ] = await Promise.all([
        api.get(
          `/edm/edge_devices/${edgeDeviceId}`,
          {
            include:
              "status, type, specifications, software_version, software_version.specifications, add_on, tenant, usergroup, technical_user, default_node",
          },
          false,
        ),
        api.getAll(`/edm/edge_devices/${edgeDeviceId}/field_gateways`, {
          include: "status, type, specifications, software_version",
        }),
        api.getAll(`/edm/edge_devices/${edgeDeviceId}/network_interfaces`),
        api.getAll(`/edm/edge_devices/${edgeDeviceId}/applications`, {
          order_by: "name",
        }),
        api.getAll("/edm/update_schedules", {
          include: "status, software_version",
          edge_device_id: edgeDeviceId,
          order_by: "-updated_at",
        }),
      ]);

      const data = transformEdgeDeviceData(
        edgeJson,
        fieldGateways,
        networkInterfaces,
        edgeDeviceApplications,
        updateSchedules,
        user,
      );

      setEdgeDevice(data);
    } catch (error) {
      notifier.showError(api.translateError(error));
    }
  };

  React.useEffect(() => {
    loadEdgeDevice();
  }, [edgeDeviceId]);

  const handleOnConfirmDelete = () => {
    unlinkEdgeDevice({ api, notifier, intl, browser, edgeDeviceId });
  };

  return (
    <Loader loading={!edgeDevice}>
      <Container>
        <Row>
          <Column>
            <ActionBar>
              <Heading
                id="edge-device-details-header"
                title={intl.formatMessage({ id: "edge_device_details.header" })}
              />
              <ActionBarButtons>
                <EditActionButton
                  id="edge-device-edit-button"
                  disabled={!accessRights.canUpdate}
                  target={`/edge_devices/${edgeDeviceId}/edit`}
                />
                {(edgeDevice as $TSFixMe)?.add_on?.product_code === "uplink" &&
                accessRights.canPermit &&
                flipper.edgeDeviceLateBinding ? (
                  <DeleteActionButton
                    id="edge-device-remove-button"
                    disabled={!accessRights.canUpdate}
                    label={intl.formatMessage({ id: "edge_device.delete" })}
                    modalTitle={intl.formatMessage({
                      id: "edge_device.delete.modal_header",
                    })}
                    modalMessage={intl.formatMessage({
                      id: "edge_device.delete.modal_message",
                    })}
                    onConfirm={handleOnConfirmDelete}
                  />
                ) : null}
              </ActionBarButtons>
            </ActionBar>
            {edgeDevice && (
              <Details>
                {renderEdgeDevicePicture(edgeDevice)}
                <DetailsHeaderItem
                  id="edge-device-serialnumber"
                  translationKey="label.serial_number"
                  value={edgeDevice.serial_number}
                />
                <DetailsHeaderItem
                  id="edge-device-name"
                  translationKey="label.name"
                  value={edgeDevice.name}
                />
                <DetailsHeaderItem
                  id="edge-device-type"
                  translationKey="label.type"
                  value={edgeDevice?.type?.name}
                />
                {renderEdgeDeviceStatus(edgeDevice)}
                {renderEdgeDeviceNotifications(edgeDevice, intl)}
                {renderEdgeDeviceLoglevel(edgeDevice, intl)}
                <DetailsItem
                  id="edge-device-description"
                  translationKey="label.description"
                  value={edgeDevice.description}
                />
                {renderEdgeDeviceLastSeenAt(edgeDevice)}
                {renderEdgeDeviceTenant(edgeDevice)}
                <DetailsItem
                  id="edge-device-usergroup"
                  translationKey="label.usergroup"
                  value={edgeDevice.usergroup}
                />
                {renderEdgeDeviceNodes(edgeDevice)}
                {renderEdgeDeviceLastApplied(edgeDevice)}
                {renderEdgeDeviceSoftwareVersion(edgeDevice)}
                {Array.isArray(edgeDevice.updateSchedules) &&
                  edgeDevice.updateSchedules.length > 0 && (
                    <More
                      // @ts-expect-error TS(2322) FIXME: Type '{ children: Element; id: string; label: { op... Remove this comment to see the full error message
                      id="edge-device-update-schedules-more"
                      label={{
                        openId: "edge-device_software-updates",
                        closeId: "edge-device_software-updates",
                      }}
                    >
                      <UpdateSchedules
                        // @ts-expect-error TS(2322) FIXME: Type '{ accessRights: accessRightsShape; updateSch... Remove this comment to see the full error message
                        accessRights={accessRights}
                        updateSchedules={edgeDevice.updateSchedules}
                        edgeDevice={edgeDevice}
                      />
                    </More>
                  )}
                {(edgeDevice.ethernetip_supported ||
                  edgeDevice.profinet_supported) && (
                  <h3 id="connectivity_settings-header">
                    {intl.formatMessage({
                      id: "edge_device_details.connectivity_settings",
                    })}
                  </h3>
                )}
                {renderEipActivationStatus(edgeDevice)}
                {renderProfinetActivationStatus(edgeDevice)}
                {edgeDevice &&
                  isSGC500Device(edgeDevice) &&
                  !edgeDevice.api_subscription &&
                  flipper?.edgeDeviceActiveFeatures && (
                    <>
                      <h3 id="data_collection-header">
                        {intl.formatMessage({ id: "label.data_collection" })}
                      </h3>
                      <DetailsItem
                        id="netilion_health-activation-status"
                        translationKey="label.netilion_health.activation_status"
                        icon={fieldGatewayEnableStatusIconClassName(
                          edgeDevice.netilion_health_active,
                        )}
                      >
                        <FormattedMessage
                          id={showLabelActiveInactive(
                            edgeDevice.netilion_health_active,
                          )}
                        />
                      </DetailsItem>
                      <DetailsItem
                        id="netilion_value-activation-status"
                        translationKey="label.netilion_value.activation_status"
                        icon={fieldGatewayEnableStatusIconClassName(
                          edgeDevice.netilion_value_active,
                        )}
                      >
                        <FormattedMessage
                          id={showLabelActiveInactive(
                            edgeDevice.netilion_value_active,
                          )}
                        />
                      </DetailsItem>
                    </>
                  )}
                {edgeDevice &&
                  isSGC500Device(edgeDevice) &&
                  flipper?.edgeDeviceDataCollectionInterval && (
                    <>
                      <h3 id="data_collection_interval-header">
                        {intl.formatMessage({
                          id: "label.data_collection_interval",
                        })}
                      </h3>
                      <DetailsItem
                        id="health-data-collection-interval"
                        translationKey="label.health_data_collection_interval"
                        value={showLabelMinuteSGC500(
                          intl,
                          edgeDevice.health_data_collection_interval,
                        )}
                        contextHelperTitle={intl.formatMessage({
                          id: "edge_device_details.connectivity_settings.health_data_collection_interval.hint_heading",
                        })}
                        contextHelperChildren={`${intl.formatMessage({
                          id: "edge_device_details.connectivity_settings.health_data_collection_interval.hint_text",
                        })}\n${intl.formatMessage({
                          id: "edge_device_details.connectivity_settings.health_data_collection_interval.hint_text2",
                        })}`}
                      />
                      <DetailsItem
                        id="measurement-value-data-collection-interval"
                        translationKey="label.measurement_value_data_collection_interval"
                        value={showLabelMinuteSGC500(
                          intl,
                          edgeDevice.measurement_value_data_collection_interval,
                        )}
                        contextHelperTitle={intl.formatMessage({
                          id: "edge_device_details.connectivity_settings.measurement_value_data_collection_interval.hint_heading",
                        })}
                        contextHelperChildren={`${intl.formatMessage({
                          id: "edge_device_details.connectivity_settings.measurement_value_data_collection_interval.hint_text",
                        })}\n${intl.formatMessage({
                          id: "edge_device_details.connectivity_settings.measurement_value_data_collection_interval.hint_text2",
                        })}`}
                      />
                    </>
                  )}
                {edgeDevice.api_subscription && (
                  <DetailsItem
                    id="netilion-connect-client-application-name"
                    translationKey="label.connect_client_application"
                    value={edgeDevice.client_application?.name}
                  />
                )}
              </Details>
            )}
            {renderFieldGateways(edgeDevice)}
            {areNetworkInterfacesEnabled(edgeDevice) && (
              <NetworkInterfaces
                networkInterfaces={edgeDevice.networkInterfaces}
                edgeDevice={edgeDevice}
              />
            )}
            {edgeDevice && isSGC200Device(edgeDevice) && (
              <>
                <h2 id="connectivity_settings-header">
                  {intl.formatMessage({
                    id: "edge_device_details.connectivity_settings",
                  })}
                </h2>
                <DetailsItem
                  id="data-collection-interval"
                  translationKey="label.data_collection_interval"
                  value={showLabelMinuteSGC200(
                    intl,
                    edgeDevice.data_collection_interval,
                  )}
                />
                <DetailsItem
                  id="data-send-interval"
                  translationKey="label.data_send_interval"
                  value={showLabelMinuteSGC200(
                    intl,
                    edgeDevice.data_send_interval,
                  )}
                />
                {/* temporarily disabled because of LIH-6726 and LIH-6727 */}
                {/* <DetailsItem
          id="data-send-start-time"
          translationKey="label.data_send_start_time"
          className="hidden"
          value={edgeDevice && edgeDevice.data_send_start_time ? edgeDevice.data_send_start_time : '-'}
        /> */}
                {/* temporarily disabled because of LIH-6726 and LIH-6727 */}
                {/* <DetailsItem
          id="data-send-environment"
          translationKey="label.data_send_environment"
          className="hidden"
          value={edgeDevice && edgeDevice.data_send_environment ? edgeDevice.data_send_environment : '-'}
        /> */}
              </>
            )}
            {edgeDevice?.isThirdPartyType
              ? null
              : edgeDevice?.api_subscription && (
                  <EdgeDeviceApplications
                    edgeDeviceApplications={edgeDevice.edgeDeviceApplications}
                    // @ts-expect-error TS(2322) FIXME: Type '{ edgeDeviceApplications: any; accessRights:... Remove this comment to see the full error message
                    accessRights={accessRights}
                  />
                )}
            {edgeDevice?.type?.manageable ? (
              <EdgeDeviceDetailsTechnicalUser
                initialTechnicalUser={edgeDevice?.technical_user}
                edgeDevice={edgeDevice}
                intl={intl}
                accessRights={accessRights}
                api={api}
                // @ts-expect-error TS(2322) FIXME: Type '{ initialTechnicalUser: any; edgeDevice: und... Remove this comment to see the full error message
                browser={browser}
                user={user}
                notifier={notifier}
              />
            ) : null}
          </Column>
        </Row>
      </Container>
    </Loader>
  );
}

export default withUser(
  // @ts-expect-error TS(2345) FIXME: Argument of type 'ForwardRefExoticComponent<Omit<W... Remove this comment to see the full error message
  withIntl(
    // @ts-expect-error TS(2345) FIXME: Argument of type 'ForwardRefExoticComponent<Omit<W... Remove this comment to see the full error message
    withFlipper(
      withSession(
        // @ts-expect-error TS(2345) FIXME: Argument of type '{ (props: Readonly<Omit<Readonly... Remove this comment to see the full error message
        withApi(
          withNotifier(
            withBrowser(withAccessRights(EdgeDeviceDetails, "EDM::EdgeDevice")),
          ),
        ),
      ),
    ),
  ),
);
